<template>
  <div :class="['chat-container', { open: isOpen }]">
    <transition name="toggle-chat" mode="out-in">
      <ChatWindow v-if="isOpen" class="chat-window" @close="toggle">
        <template #header>
          <ChatHeader
            :recipient-id="recipientId"
            :sender="sender"
            :recipients="recipients"
            @close="toggle"
          />
        </template>
        <template #content>
          <ChatContent
            :recipient-id="recipientId"
            :sender="sender"
            :comments="comments"
            :recipients="recipients"
          />
        </template>
        <template #actions>
          <ChatActions
            :emoji-index="emojiIndex"
            :emojis-enabled="emojisEnabled"
            :is-preview="isPreview"
            @send="handleSend"
          />
        </template>
      </ChatWindow>
    </transition>
    <!-- UNSURE if this could be moved outside of the transion...-->
    <ChatSystemMessage
      v-if="systemMessageOpen"
      class="chat-system-message"
      :sender="sender"
      @click="toggle"
      @close="handleSystemMessageClose"
    />
    <ChatFab
      class="hidden-xs-and-down"
      :open="isOpen"
      :is-preview="isPreview"
      :new-comments-count="newCommentsCount"
      :theme="theme"
      @click="toggle"
    />
  </div>
</template>

<script lang="ts">
import ChatActions from '@getaccept/lib-shared-new/src/chat/components/ChatActions.vue';
import ChatFab from '@getaccept/lib-shared-new/src/chat/components/ChatFab.vue';
import type { ChatComment } from '@getaccept/lib-shared-new/src/chat/types/chat-comment';
import { EmojiHelper } from '@getaccept/lib-shared-new/src/emoji/helpers/emoji.helper';
import type { SigningTheme } from '@getaccept/lib-shared-new/src/signing-theme/types/theme';
import type { SigningPageRecipient } from '@getaccept/lib-shared-new/src/types/signing-page-recipient';
import type { SigningPageUser } from '@getaccept/lib-shared-new/src/types/signing-page-user';
import type { PropType } from 'vue';
import { defineComponent, onMounted, ref } from 'vue';
import type { EmojiIndex } from 'emoji-mart-vue-fast';
import ChatContent from './ChatContent.vue';
import ChatHeader from './ChatHeader.vue';
import ChatSystemMessage from './ChatSystemMessage.vue';
import ChatWindow from './ChatWindow.vue';

export default defineComponent({
  components: {
    ChatFab,
    ChatWindow,
    ChatActions,
    ChatHeader,
    ChatContent,
    ChatSystemMessage,
  },
  props: {
    recipientId: {
      type: String,
      required: true,
    },
    sender: {
      type: Object as PropType<SigningPageUser>,
      required: true,
    },
    comments: {
      type: Array as PropType<ChatComment[]>,
      required: true,
    },
    recipients: {
      type: Array as PropType<SigningPageRecipient[]>,
      required: true,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    isPreview: {
      type: Boolean,
      default: false,
    },
    newCommentsCount: {
      type: Number,
      required: true,
    },
    theme: {
      type: Object as PropType<SigningTheme>,
      required: false,
      default: undefined,
    },
    systemMessageOpen: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const emojisEnabled = ref(true);
    const emojiIndex = ref<EmojiIndex>(null);

    const handleSystemMessageClose = () => {
      emit('closeSystemMessage');
    };

    const toggle = () => {
      emit('toggleChat', !props.isOpen);
    };

    const handleSend = (text: string) => {
      emit('send', text);
    };

    onMounted(async () => {
      emojiIndex.value = await EmojiHelper.emojiIndex();
    });

    return {
      emojisEnabled,
      emojiIndex,
      handleSystemMessageClose,
      toggle,
      handleSend,
    };
  },
});
</script>

<style lang="scss" scoped>
.chat-container {
  position: relative;
  transition: background 0.3s ease-out;

  &.open {
    @media only screen and (max-width: $xs), (max-height: $xs) and (orientation: landscape) {
      background: rgba(#000, 0.55);
      height: 100%;
      width: 100%;
      position: fixed;
      bottom: 0;
    }
  }
}

.toggle-chat-leave-to,
.toggle-chat-enter-from {
  opacity: 0;
  transform: scale(0.9);

  @media only screen and (max-width: $sm), (max-height: $xs) and (orientation: landscape) {
    opacity: 1;
    transform: initial;
  }

  @media only screen and (max-width: $xs), (max-height: $xs) and (orientation: landscape) {
    top: 100%;
  }
}

.toggle-chat-leave-from,
.toggle-chat-enter-to {
  opacity: 1;

  @media only screen and (max-width: $xs), (max-height: $xs) and (orientation: landscape) {
    top: 5%;
  }
}

.toggle-chat-enter-active,
.toggle-chat-leave-active {
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out, top 0.4s ease-in-out;
}

.chat-window,
.chat-system-message {
  transform-origin: bottom right;
}
</style>
